/**
 * Sound Store
 * Reactive state management for audio system
 */

import { writable, derived } from 'svelte/store';
import type { SoundState, SoundInstance, SoundCategory } from '../types/audio';
import { SoundCategory as SC } from '../types/audio';
import { loadAudioPreferences, saveAudioPreferences } from '../lib/audioPreferences';

const initialPrefs = loadAudioPreferences();

const initialState: SoundState = {
  activeSounds: new Map(),
  masterVolume: initialPrefs.masterVolume,
  isMuted: initialPrefs.isMuted,
  categoryVolumes: new Map([
    [SC.MUSIC, initialPrefs.categoryVolumes[SC.MUSIC]],
    [SC.SFX, initialPrefs.categoryVolumes[SC.SFX]],
    [SC.AMBIENT, initialPrefs.categoryVolumes[SC.AMBIENT]],
    [SC.UI, initialPrefs.categoryVolumes[SC.UI]],
    [SC.VOICE, initialPrefs.categoryVolumes[SC.VOICE]],
  ]),
  isInitialized: false,
  isSuspended: true,
};

function createSoundStore() {
  const { subscribe, set, update } = writable<SoundState>(initialState);

  return {
    subscribe,

    /**
     * Set master volume (0.0 to 1.0)
     */
    setMasterVolume(volume: number) {
      update((state) => {
        const newState = { ...state, masterVolume: volume };
        saveAudioPreferences({
          masterVolume: volume,
          isMuted: state.isMuted,
          categoryVolumes: Object.fromEntries(state.categoryVolumes) as any,
        });
        return newState;
      });
    },

    /**
     * Get master volume
     */
    getMasterVolume(): number {
      let vol = 1.0;
      update((state) => {
        vol = state.masterVolume;
        return state;
      });
      return vol;
    },

    /**
     * Set muted state
     */
    setMuted(muted: boolean) {
      update((state) => {
        const newState = { ...state, isMuted: muted };
        saveAudioPreferences({
          masterVolume: state.masterVolume,
          isMuted: muted,
          categoryVolumes: Object.fromEntries(state.categoryVolumes) as any,
        });
        return newState;
      });
    },

    /**
     * Toggle mute
     */
    toggleMute() {
      update((state) => {
        const newMuted = !state.isMuted;
        saveAudioPreferences({
          masterVolume: state.masterVolume,
          isMuted: newMuted,
          categoryVolumes: Object.fromEntries(state.categoryVolumes) as any,
        });
        return { ...state, isMuted: newMuted };
      });
    },

    /**
     * Set category volume (0.0 to 1.0)
     */
    setCategoryVolume(category: SoundCategory, volume: number) {
      update((state) => {
        const newVolumes = new Map(state.categoryVolumes);
        newVolumes.set(category, volume);
        saveAudioPreferences({
          masterVolume: state.masterVolume,
          isMuted: state.isMuted,
          categoryVolumes: Object.fromEntries(newVolumes) as any,
        });
        return { ...state, categoryVolumes: newVolumes };
      });
    },

    /**
     * Add active sound instance
     */
    addActiveSound(id: string, instance: SoundInstance) {
      update((state) => {
        const newSounds = new Map(state.activeSounds);
        newSounds.set(id, instance);
        return { ...state, activeSounds: newSounds };
      });
    },

    /**
     * Remove active sound instance
     */
    removeActiveSound(id: string) {
      update((state) => {
        const newSounds = new Map(state.activeSounds);
        newSounds.delete(id);
        return { ...state, activeSounds: newSounds };
      });
    },

    /**
     * Set initialized state
     */
    setInitialized(initialized: boolean) {
      update((state) => ({ ...state, isInitialized: initialized }));
    },

    /**
     * Set suspended state
     */
    setSuspended(suspended: boolean) {
      update((state) => ({ ...state, isSuspended: suspended }));
    },

    /**
     * Reset to initial state
     */
    reset() {
      set(initialState);
    },
  };
}

export const soundStore = createSoundStore();

// Derived stores
export const isMuted = derived(soundStore, ($store) => $store.isMuted);

export const masterVolume = derived(soundStore, ($store) => $store.masterVolume);

export const isAudioReady = derived(
  soundStore,
  ($store) => $store.isInitialized && !$store.isSuspended
);

export const activeSoundCount = derived(
  soundStore,
  ($store) => $store.activeSounds.size
);

export const activeSoundsByCategory = derived(soundStore, ($store) => {
  const byCategory = new Map<SoundCategory, number>();

  $store.activeSounds.forEach((instance) => {
    const count = byCategory.get(instance.category) || 0;
    byCategory.set(instance.category, count + 1);
  });

  return byCategory;
});
